// The hub — pick your first source (overhaul §6.2, panel 02). Renders every live source
// from the HUB_SOURCES table, split into a "Recommended to start" shortlist and an "All
// sources" group so onboarding reflects the full breadth of what Terum captures while still
// steering new users toward the highest-signal, easiest connects. Chat sources carry the
// EXT badge (they need the browser extension); Slack/Notion carry a TEAM badge (team-
// workspace installs — a team is created on the fly if the user has none). Sign out is the
// only escape from the hard-lock until a source connects.

import { siteLabel } from '../../lib/sites';
import { SourceIcon } from '../shared/SourceIcon';
import { HUB_SOURCES, type HubSource } from './wizard-step';

function SrcTile({ src, onPick }: { src: HubSource; onPick: (id: string) => void }) {
  return (
    <button type="button" className="wiz-src" onClick={() => onPick(src.id)}>
      <span className="ltile"><SourceIcon id={src.id} size={22} /></span>
      {siteLabel(src.id)}
      {src.ext && <span className="ext-tag">EXT</span>}
      {src.team && (
        <span
          className="ext-tag"
          style={{ background: 'var(--acc-soft)', color: 'var(--acc)' }}
          title="Team-wide install - sets up your team if you don't have one yet"
        >
          TEAM
        </span>
      )}
    </button>
  );
}

export function SourceHub({ onPick, onBack, onSignOut }: {
  onPick: (id: string) => void;
  onBack: () => void;
  onSignOut: () => void;
}) {
  const recommended = HUB_SOURCES.filter((s) => s.recommended);
  const others = HUB_SOURCES.filter((s) => !s.recommended);

  return (
    <>
      <div className="crumb">STEP 02</div>
      <div className="wh">Connect your <span className="em">first source</span>.</div>
      <p className="wp">
        One is enough to enter - add the rest later. You'll confirm what Terum may read. Chat
        sources (<span className="ext-tag">EXT</span>) need the browser extension;
        {' '}<span className="ext-tag" style={{ background: 'var(--acc-soft)', color: 'var(--acc)' }}>TEAM</span>
        {' '}sources install for your whole team; the rest connect on their own.
      </p>

      <div className="section-note" style={{ marginTop: 4, marginBottom: 8 }}>Recommended to start</div>
      <div className="wiz-src-grid">
        {recommended.map((s) => <SrcTile key={s.id} src={s} onPick={onPick} />)}
      </div>

      <div className="section-note" style={{ marginTop: 16, marginBottom: 8 }}>All sources</div>
      <div className="wiz-src-grid">
        {others.map((s) => <SrcTile key={s.id} src={s} onPick={onPick} />)}
      </div>

      <div className="section-note" style={{ marginTop: 14 }}>
        Chat sources (<span className="ext-tag">EXT</span>) route to a quick "add the extension"
        step <i>only if it isn't already installed</i> - then back to finish connecting. Google,
        GitHub, Claude Code &amp; Granola connect directly.
      </div>
      <div className="wiz-foot">
        <button className="btn btn-secondary" onClick={onBack}>Back</button>
        <a style={{ cursor: 'pointer', color: 'var(--inkS)', fontSize: 13 }} onClick={onSignOut}>Sign out</a>
        <span className="prog">Step 2 of 4</span>
      </div>
    </>
  );
}
